import { useEffect } from 'react';

/**
 * toast() (:726) — the prototype's one-line confirmation bar.
 *
 *   function toast(msg){ t.textContent=msg; t.classList.add("show");
 *     clearTimeout(t._h); t._h=setTimeout(()=>t.classList.remove("show"),2600) }
 *
 * The CSS for this was ported in Phase 3 (base.css:283) and has been sitting
 * unused ever since: the prototype calls toast() dozens of times and nothing
 * here could. Every "Booked!", "Copied", "Assignment submitted" confirmation
 * was silently missing.
 *
 * 2600ms and the re-arm on each message are the prototype's, verbatim — a
 * second toast replaces the first and restarts the clock rather than queueing.
 *
 * role="status" + aria-live="polite" is not in the prototype, which renders a
 * bare div: a confirmation nobody announces is invisible to a screen reader,
 * and this is the only feedback several actions give. Non-visual addition, no
 * layout change.
 */
export function Toast({ message, onDone }: { message: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    const h = setTimeout(onDone, 2600);
    return () => clearTimeout(h);
  }, [message, onDone]);

  return (
    <div
      className={`pointer-events-none fixed bottom-[26px] left-1/2 z-[200] max-w-[92vw] -translate-x-1/2 rounded-xl bg-[var(--ink)] px-[22px] py-[13px] text-center text-[14.5px] font-semibold text-white shadow-[var(--shadow-lg)] transition-all duration-300 ${
        message ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
      }`}
      id="toast"
      data-testid="toast"
      role="status"
      aria-live="polite"
    >
      {message}
    </div>
  );
}
